import { useEffect, useRef } from 'react';

// Keeps the screen awake while `active` is true, using the Screen Wake Lock API.
// Silently no-ops on browsers that don't support it.
export default function useWakeLock(active) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (typeof navigator === 'undefined') return undefined;
    if (!('wakeLock' in navigator)) return undefined;

    let cancelled = false;

    const acquire = async () => {
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (cancelled) {
          sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
        sentinel.addEventListener('release', () => {
          // released either by user or by visibility loss
          sentinelRef.current = null;
        });
      } catch {
        /* permission denied or page hidden */
      }
    };

    const release = () => {
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s) s.release().catch(() => {});
    };

    if (active) {
      acquire();
      // re-acquire after returning to the tab
      const onVisibility = () => {
        if (document.visibilityState === 'visible' && active && !sentinelRef.current) {
          acquire();
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      return () => {
        cancelled = true;
        document.removeEventListener('visibilitychange', onVisibility);
        release();
      };
    }
    release();
    return undefined;
  }, [active]);
}
